import { useEffect, useState } from 'react';
import { observer } from 'mobx-react';
import { Avatar, Dropdown, Button, Grid, Tooltip, type MenuProps } from 'antd';
import {
  SettingOutlined,
  LogoutOutlined,
  UserOutlined,
  SearchOutlined,
  MoonOutlined,
  SunOutlined,
  QuestionCircleOutlined,
  HistoryOutlined,
  StarFilled,
  AppstoreOutlined,
  DownOutlined,
} from '@ant-design/icons';
import { Outlet, useLocation, useNavigate, Link } from 'react-router-dom';
import { useStore } from '@/stores';
import { CommandPalette } from '@/components/CommandPalette';
import { HelpDrawer } from '@/features/help/HelpDrawer';
import { KeyboardCheatsheet } from '@/components/KeyboardCheatsheet';
import { BreadcrumbNav } from '@/components/BreadcrumbNav';
import { EnvBanner } from '@/components/EnvBanner';
import { MerchantRailCard } from '@/components/MerchantRailCard';
import { PlatformRailCard } from '@/components/PlatformRailCard';
import { ModuleLauncherDrawer } from '@/components/ModuleLauncherDrawer';
import { installGlobalShortcuts } from '@/lib/shortcuts';
import { colors } from '@/theme/tokens';
import { NavRail } from './NavRail';
import { findDestination } from './navigation';

// Top-level layout — the module shell (2026-11-01):
//
//   ┌── header: brand · [Modules ▾ Evaluation] · ⌘K search · theme · help · you ──┐
//   │ module rail          │ breadcrumbs: Home / merchant / module / page        │
//   │ merchant card        │ routed page                                         │
//   │ Home                 │                                                     │
//   │ EVALUATION           │                                                     │
//   │   its destinations   │                                                     │
//   │ Pinned               │                                                     │
//   │ platform status card │                                                     │
//   │ footer               │                                                     │
//   └──────────────────────┴─────────────────────────────────────────────────────┘
//
// Five database-backed modules (Agent Design, Knowledge & Resources, Evaluation,
// Operations, Administration) replace the old 29-item two-rail catalogue: the
// rail shows only the module that owns the URL, and the header's Modules button
// opens the Provider Web launcher for the rest. Home stays global. Everything
// navigational reads ONE resolved model (StudioModuleStore); the merchant card
// stays the practice context, separate from module identity.
//
// The routed page (<Outlet/>) is rendered unconditionally — loading, refreshing
// or failing to load the catalog never remounts it, so drafts and running jobs
// survive, and a deep link renders under its own guards whatever the catalog says.

export const AppShell = observer(function AppShell() {
  const { authStore, merchantContextStore: ctx, themeStore, navStore, commandPaletteStore, studioModuleStore: sm } = useStore();
  const location = useLocation();
  const navigate = useNavigate();
  const screens = Grid.useBreakpoint();
  const [cheatsheetOpen, setCheatsheetOpen] = useState(false);
  const [launcherOpen, setLauncherOpen] = useState(false);

  useEffect(() => {
    installGlobalShortcuts(() => setCheatsheetOpen(true));
  }, []);

  // Hydrate the merchants list on first mount of the authenticated shell.
  //
  // Why here: AppShell wraps every authenticated route — Dashboard, Agents,
  // Knowledge, deep-linked detail pages, anything. Without this, the merchants
  // list only loaded when the user happened to visit Dashboard or All
  // merchants, or opened the picker drawer. So a user who refreshed
  // /knowledge/123 (or hit a bookmark, or pasted a URL into a new tab) would
  // see the merchant card stuck on "Pick a merchant" — selectedMerchantId was
  // restored from session storage and used by API filters, but the
  // merchants array was empty so selectedMerchant.title resolved to undefined.
  // Long-standing user-reported bug; fix is to make hydration unconditional
  // at the shell level. loadMerchants() is idempotent + cheap, and the guard
  // below makes a navigation between routes a no-op.
  useEffect(() => {
    if (ctx.merchants.length === 0 && !ctx.isLoading) {
      void ctx.loadMerchants();
    }
    // Run-once on mount; observer re-renders pick up the populated list.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // This person's navigation: loaded once per identity (a sign-in as someone else starts empty).
  useEffect(() => {
    sm.ensureLoaded();
  }, [sm, sm.status, authStore.user?.userId]);

  // The active module follows the URL; remember it for pages that belong to no module (Home).
  const placement = sm.placementFor(location.pathname);
  const urlModuleKey = placement.kind === 'module' ? placement.module.key : null;
  useEffect(() => {
    if (urlModuleKey) sm.rememberModule(urlModuleKey);
  }, [urlModuleKey, sm]);

  const isDark = themeStore.mode === 'dark';
  const surfaceBg = isDark ? colors.slate[800] : '#FFFFFF';
  const contentBg = isDark ? colors.slate[900] : colors.slate[50];
  const borderColor = isDark ? colors.slate[700] : colors.slate[200];
  const merchantSelected = ctx.selectedMerchantId != null;
  const activeKey = findDestination(location.pathname)?.key ?? '';
  const railModule = sm.railModule(location.pathname);
  const headerModule = placement.kind === 'module' ? placement.module : null;
  // Narrow screens: icon rail, compact header.
  const narrow = !screens.md;

  const userMenu: MenuProps['items'] = [
    { key: 'profile', icon: <UserOutlined />, label: authStore.user?.displayName ?? 'Profile', disabled: true },
    {
      key: 'theme',
      icon: isDark ? <SunOutlined /> : <MoonOutlined />,
      label: isDark ? 'Light mode' : 'Dark mode',
      onClick: () => themeStore.toggleMode(),
    },
    { key: 'shortcuts', icon: <QuestionCircleOutlined />, label: 'Keyboard shortcuts', onClick: () => setCheatsheetOpen(true) },
    // The catalog administrators' way back to Administration → Modules, whatever the catalog currently shows.
    ...(sm.nav?.canManageModules
      ? [{ key: 'modules', icon: <AppstoreOutlined />, label: 'Manage modules', onClick: () => navigate('/platform/modules') }]
      : []),
    { key: 'settings', icon: <SettingOutlined />, label: 'Settings', disabled: true },
    { type: 'divider' as const },
    { key: 'logout', icon: <LogoutOutlined />, label: 'Sign out', onClick: () => authStore.logout() },
  ];

  const leftWide = !narrow && navStore.isWide('left');

  return (
    <div style={{ height: '100vh', display: 'flex', flexDirection: 'column', background: contentBg }}>
      <EnvBanner />

      <header
        style={{
          height: 60,
          flexShrink: 0,
          background: surfaceBg,
          borderBottom: `1px solid ${borderColor}`,
          display: 'flex',
          alignItems: 'center',
          gap: 12,
          padding: '0 16px 0 14px',
        }}
      >
        <Link to="/" aria-label="Agent Studio — Home" style={{ display: 'flex', alignItems: 'center', gap: 10, textDecoration: 'none', width: narrow ? 'auto' : 200 - 14, flexShrink: 0 }}>
          <span
            style={{
              width: 30,
              height: 30,
              borderRadius: 9,
              display: 'grid',
              placeItems: 'center',
              color: '#FFFFFF',
              fontSize: 14,
              background: `linear-gradient(135deg, ${colors.violet[500]}, ${colors.ink[600]})`,
            }}
          >
            <StarFilled />
          </span>
          {!narrow && (
            <span style={{ fontSize: 15, fontWeight: 700, letterSpacing: '-0.01em', color: isDark ? colors.slate[50] : colors.slate[900] }}>
              Agent Studio
            </span>
          )}
        </Link>

        <Tooltip title={headerModule ? `Modules — you are in ${headerModule.title}` : 'Modules'}>
          <Button
            onClick={() => setLauncherOpen(true)}
            aria-haspopup="dialog"
            aria-expanded={launcherOpen}
            aria-label={headerModule ? `Modules. Current module: ${headerModule.title}` : 'Modules'}
            icon={<AppstoreOutlined />}
            style={{ flexShrink: 0, maxWidth: narrow ? 44 : 240 }}
          >
            {!narrow && (
              <>
                <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 170 }}>{headerModule?.title ?? 'Modules'}</span>
                <DownOutlined style={{ fontSize: 10 }} />
              </>
            )}
          </Button>
        </Tooltip>

        <div style={{ flex: 1, display: 'flex', justifyContent: 'center', minWidth: 0 }}>
          <SearchPill isDark={isDark} borderColor={borderColor} compact={narrow} />
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <Tooltip title={isDark ? 'Switch to light mode' : 'Switch to dark mode'}>
            <Button
              type="text"
              shape="circle"
              icon={isDark ? <SunOutlined /> : <MoonOutlined />}
              onClick={() => themeStore.toggleMode()}
              aria-label="Toggle theme"
            />
          </Tooltip>

          <Tooltip title="Keyboard shortcuts · ?">
            <Button
              type="text"
              shape="circle"
              icon={<QuestionCircleOutlined />}
              onClick={() => setCheatsheetOpen(true)}
              aria-label="Keyboard shortcuts"
            />
          </Tooltip>

          <Dropdown menu={{ items: userMenu }} placement="bottomRight" trigger={['click']}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', padding: '4px 8px', borderRadius: 8 }}>
              <Avatar size={30} style={{ background: `linear-gradient(135deg, ${colors.violet[500]}, ${colors.ink[600]})` }}>
                {(authStore.user?.displayName?.[0] ?? 'U').toUpperCase()}
              </Avatar>
              {!narrow && (
                <div style={{ fontSize: 13, fontWeight: 500, color: isDark ? colors.slate[100] : colors.slate[800] }}>
                  {authStore.user?.displayName ?? 'User'}
                </div>
              )}
            </div>
          </Dropdown>
        </div>
      </header>

      <div style={{ flex: 1, minHeight: 0, display: 'flex' }}>
        <NavRail
          module={railModule}
          activeKey={activeKey}
          isDark={isDark}
          merchantSelected={merchantSelected}
          forceSlim={narrow}
          onOpenLauncher={() => setLauncherOpen(true)}
          topCard={<MerchantRailCard wide={leftWide} isDark={isDark} />}
          contextCard={<PlatformRailCard wide={leftWide} isDark={isDark} />}
          footer={
            <div
              style={{
                borderTop: `1px solid ${borderColor}`,
                padding: leftWide ? '6px 8px' : '6px 6px',
                display: 'flex',
                flexDirection: leftWide ? 'row' : 'column',
                gap: 2,
              }}
            >
              {/* Recent opens ⌘K, whose first group IS your recents. */}
              <FooterButton icon={<HistoryOutlined />} label="Recent" short="Recent" wide={leftWide} isDark={isDark} onClick={() => commandPaletteStore.open()} />
              <FooterButton
                icon={isDark ? <SunOutlined /> : <MoonOutlined />}
                label={isDark ? 'Light mode' : 'Dark mode'}
                short="Theme"
                wide={leftWide}
                isDark={isDark}
                onClick={() => themeStore.toggleMode()}
              />
              <FooterButton icon={<QuestionCircleOutlined />} label="Keyboard shortcuts" short="Keys" wide={leftWide} isDark={isDark} onClick={() => setCheatsheetOpen(true)} />
            </div>
          }
        />

        <main
          style={{ flex: 1, minWidth: 0, overflow: 'auto', display: 'flex', flexDirection: 'column' }}
          // A click in the page settles an outstanding left-rail "peek" (provider-web rule).
          onMouseDown={() => navStore.contentClicked()}
        >
          <div style={{ padding: narrow ? '10px 12px 0' : '12px 24px 0' }}>
            <BreadcrumbNav />
          </div>
          <div style={{ padding: narrow ? 12 : 24, flex: 1 }}>
            <Outlet />
          </div>
        </main>
      </div>

      <ModuleLauncherDrawer open={launcherOpen} onClose={() => setLauncherOpen(false)} />
      <CommandPalette />
      <KeyboardCheatsheet open={cheatsheetOpen} onClose={() => setCheatsheetOpen(false)} />
      <HelpDrawer />
    </div>
  );
});

// ── Search pill (top bar) ────────────────────────────────────────────────────

const SearchPill = observer(function SearchPill({
  isDark,
  borderColor,
  compact,
}: {
  isDark: boolean;
  borderColor: string;
  compact?: boolean;
}) {
  const { commandPaletteStore: cp } = useStore();
  if (compact) {
    return (
      <Tooltip title="Search · Cmd+K">
        <Button shape="circle" icon={<SearchOutlined />} onClick={() => cp.open()} aria-label="Open command palette" />
      </Tooltip>
    );
  }
  return (
    <Tooltip title="Search · Cmd+K">
      <button
        onClick={() => cp.open()}
        aria-label="Open command palette"
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 8,
          padding: '7px 10px 7px 14px',
          borderRadius: 999,
          border: `1px solid ${borderColor}`,
          background: isDark ? colors.slate[900] : colors.slate[50],
          color: colors.slate[500],
          fontSize: 13,
          cursor: 'pointer',
          fontFamily: 'inherit',
          width: '100%',
          maxWidth: 520,
          minWidth: 240,
          transition: 'border-color 100ms ease',
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.borderColor = colors.ink[500];
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.borderColor = borderColor;
        }}
      >
        <SearchOutlined style={{ fontSize: 13 }} />
        <span style={{ flex: 1, textAlign: 'left', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          Search agents, pages, runs, commands…
        </span>
        <kbd
          style={{
            fontSize: 10,
            padding: '1px 6px',
            borderRadius: 4,
            background: isDark ? colors.slate[700] : colors.slate[200],
            color: isDark ? colors.slate[300] : colors.slate[600],
            fontFamily: "'JetBrains Mono', monospace",
            marginLeft: 4,
          }}
        >
          ⌘K
        </kbd>
      </button>
    </Tooltip>
  );
});

// ── Footer button ────────────────────────────────────────────────────────────

function FooterButton({
  icon,
  label,
  short,
  wide,
  isDark,
  onClick,
}: {
  icon: React.ReactNode;
  label: string;
  /** Visible text on the wide rail; the full label is the tooltip. */
  short: string;
  wide: boolean;
  isDark: boolean;
  onClick: () => void;
}) {
  const [hovered, setHovered] = useState(false);
  return (
    <Tooltip title={label} placement="right">
      <button
        type="button"
        aria-label={label}
        onClick={onClick}
        onMouseEnter={() => setHovered(true)}
        onMouseLeave={() => setHovered(false)}
        style={{
          flex: wide ? 1 : undefined,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 5,
          padding: '7px 2px',
          borderRadius: 8,
          background: hovered ? (isDark ? colors.slate[800] : colors.slate[100]) : 'transparent',
          border: 'none',
          color: isDark ? colors.slate[400] : colors.slate[500],
          fontSize: 11.5,
          cursor: 'pointer',
          fontFamily: 'inherit',
          transition: 'background 80ms ease',
        }}
      >
        <span style={{ fontSize: 14, display: 'inline-flex' }}>{icon}</span>
        {wide && <span>{short}</span>}
      </button>
    </Tooltip>
  );
}
